/* Shared building blocks: Logo, Button, Field, Chip, Toggle, Segmented, Toast */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './icons.jsx';
import { VilkaMark } from './mark.jsx';

export function Logo({ size = 36, anim = 'hover', trigger, onClick, title = 'Вилка', withName = true }) {
  return (
    <div
      style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      aria-label={onClick ? 'На главную' : title}
    >
      <VilkaMark
        variant="yellow-square"
        size={size}
        anim={anim}
        trigger={trigger}
        title={title}
      />
      {withName && (
        <span style={{ fontWeight: 700, fontSize: 18, letterSpacing: '-0.01em', color: 'var(--t-text)' }}>Вилка</span>
      )}
    </div>
  );
}

export function Button({ variant = 'primary', size = 'md', icon, iconRight, children, block, ...rest }) {
  const cls = [
    'btn',
    `btn-${variant}`,
    size === 'sm' ? 'btn-sm' : '',
    block ? 'btn-block' : ''
  ].filter(Boolean).join(' ');
  return (
    <button className={cls} {...rest}>
      {icon && <Icon name={icon} size={size === 'sm' ? 14 : 16} />}
      {children}
      {iconRight && <Icon name={iconRight} size={size === 'sm' ? 14 : 16} />}
    </button>
  );
}

export function Field({ label, hint, error, children, id, ariaId }) {
  return (
    <div id={ariaId}>
      {label && <label className="field-label" htmlFor={id}>{label}</label>}
      {children}
      {error
        ? <div className="field-error">{error}</div>
        : (hint ? <div className="field-hint">{hint}</div> : null)}
    </div>
  );
}

export function Toggle({ checked, onChange, label }) {
  return (
    <div className={'toggle ' + (checked ? 'is-on' : '')} onClick={() => onChange(!checked)} role="switch" aria-checked={checked}>
      <div className="toggle-track"><div className="toggle-thumb"></div></div>
      {label && <span style={{ fontSize: 14 }}>{label}</span>}
    </div>
  );
}

export function Segmented({ value, onChange, options }) {
  return (
    <div className="segmented" role="tablist">
      {options.map((o) => (
        <button
          key={o.value}
          role="tab"
          aria-selected={value === o.value}
          className={value === o.value ? 'is-active' : ''}
          onClick={() => onChange(o.value)}
          type="button"
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* --------- Skill chips with FLIP animation on remove ---------
 * `values` is an array of stable ids (slugs in the resume case).
 * `labelOf(id)` resolves a display name; defaults to identity.
 * `isKnown(id)` gates manually-typed input — only allowed values can be added. */
export function ChipsField({ values, onChange, suggestions, glowToken, labelOf, isKnown, disabled, disabledHint }) {
  const label = labelOf || ((x) => x);
  const known = isKnown || (() => true);
  const containerRef = useRef(null);
  const prevRectsRef = useRef(new Map());

  // FLIP — measure before paint, then animate from old offset to new
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const chips = container.querySelectorAll('[data-chip]');
    const newRects = new Map();
    chips.forEach((el) => newRects.set(el.dataset.chip, el.getBoundingClientRect()));

    chips.forEach((el) => {
      const key = el.dataset.chip;
      const prev = prevRectsRef.current.get(key);
      const next = newRects.get(key);
      if (prev && next && (prev.left !== next.left || prev.top !== next.top)) {
        const dx = prev.left - next.left;
        const dy = prev.top - next.top;
        el.animate(
          [
            { transform: `translate(${dx}px, ${dy}px)` },
            { transform: 'translate(0, 0)' }
          ],
          { duration: 240, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
        );
      }
    });
    prevRectsRef.current = newRects;
  }, [values]);

  // The slug of the most-recently-added chip — gets the `chip-fresh-flash`
  // class for one render, then drops it. Used by both the chip-suggest path
  // (click below) and the fly-ghost path from <RecsCard> (which calls
  // onChange directly with the new value as the LAST element).
  const [freshSlug, setFreshSlug] = useState(null);
  useEffect(() => {
    if (freshSlug === null) return undefined;
    const t = setTimeout(() => setFreshSlug(null), 900);
    return () => clearTimeout(t);
  }, [freshSlug]);
  // Detect new last value to flash — covers both internal `add()` and external
  // onChange callers (the fly-ghost from <RecsCard>).
  const prevValuesLenRef = useRef(values.length);
  useEffect(() => {
    if (values.length > prevValuesLenRef.current) {
      setFreshSlug(values[values.length - 1]);
    }
    prevValuesLenRef.current = values.length;
  }, [values]);

  const add = (v) => {
    if (!v) return;
    if (values.includes(v)) return;
    if (!known(v)) return;
    onChange([...values, v]);
  };
  const remove = (v) => {
    const el = containerRef.current?.querySelector(`[data-chip="${CSS.escape(v)}"]`);
    if (el) {
      el.classList.add('chip-exit');
      setTimeout(() => onChange(values.filter((x) => x !== v)), 140);
    } else {
      onChange(values.filter((x) => x !== v));
    }
  };

  const [draft, setDraft] = useState('');

  // While typing: filter the suggestion pool by case-insensitive substring
  // match against either the slug or the human label. Rank by COVERAGE
  // (q.length / name.length) so that for "C" the short "C/C++" beats the
  // long "C# программирование", instead of both being equally "matches".
  // Within the same coverage, exact > prefix > substring is the tiebreaker
  // (so "react" surfaces over "react-native" when the user types "react").
  // Without typing: fall through to the raw `suggestions` order verbatim
  // (caller pre-ranks them — see useSkillSuggestions in splitview.jsx).
  const matched = useMemo(() => {
    const pool = (suggestions || []).filter((s) => !values.includes(s));
    const q = draft.trim().toLowerCase();
    if (!q) return pool.slice(0, 7);
    const ranked = pool
      .map((s) => {
        const name = String(label(s)).toLowerCase();
        const slug = String(s).toLowerCase();
        const target = name.length <= slug.length ? name : slug;
        let kind;
        if (slug === q || name === q) kind = 0;
        else if (slug.startsWith(q) || name.startsWith(q)) kind = 1;
        else if (slug.includes(q) || name.includes(q)) kind = 2;
        else return null;
        // Higher coverage = closer to a complete match for the typed text.
        const coverage = q.length / Math.max(target.length, 1);
        return { s, kind, coverage };
      })
      .filter(Boolean)
      .sort((a, b) => (b.coverage - a.coverage) || (a.kind - b.kind));
    return ranked.slice(0, 7).map((r) => r.s);
  }, [suggestions, values, draft, label]);

  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      // Prefer the top-ranked match for the typed text, falling back to the
      // raw input only if it happens to be a known slug. Stops users from
      // accidentally adding noise like "react.js" when "react" is what we
      // canonically have.
      const v = matched[0] || draft.trim().replace(/,$/, '');
      if (v && known(v)) {
        add(v);
        setDraft('');
      }
    } else if (e.key === 'Backspace' && !draft && values.length) {
      remove(values[values.length - 1]);
    }
  };

  const available = matched;

  return (
    <div className={'stack-3 ' + (glowToken ? 'field-glow' : '')} key={glowToken || 'static'}>
      <div
        ref={containerRef}
        data-skill-field-target
        aria-disabled={disabled || undefined}
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 8,
          background: 'var(--t-bg)', borderRadius: 12, padding: 10,
          minHeight: 56, alignItems: 'center',
          border: '2px solid transparent',
          transition: 'border-color 120ms linear, background 120ms linear, opacity 120ms linear',
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'auto',
          pointerEvents: disabled ? 'none' : 'auto',
        }}
        onClick={(e) => {
          if (disabled) return;
          if (e.target === e.currentTarget) {
            e.currentTarget.querySelector('input')?.focus();
          }
        }}
      >
        {values.map((v) => (
          <span
            key={v}
            data-chip={v}
            className={'chip chip-removable' + (v === freshSlug ? ' chip-fresh-flash' : '')}
          >
            {label(v)}
            <button className="chip-x" onClick={() => remove(v)} aria-label={`Удалить ${label(v)}`} type="button">
              <Icon name="x" size={12} stroke={2} />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          disabled={disabled}
          placeholder={
            disabled
              ? (disabledHint || '')
              : (values.length ? '' : 'Введите навык и Enter')
          }
          style={{
            flex: 1, minWidth: 140, border: 0, outline: 'none',
            background: 'transparent', fontSize: 15, padding: '6px 4px'
          }}
        />
      </div>

      {!disabled && available.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {available.map((s) => (
            <button
              key={s}
              className="chip-suggest"
              onClick={() => { add(s); setDraft(''); }}
              type="button"
            >
              {label(s)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* --------- Toast --------- */
export function Toast({ message, visible }) {
  return (
    <div className={'toast ' + (visible ? 'is-visible' : '')} role="status">
      <Icon name="check" size={16} stroke={2} />
      {message}
    </div>
  );
}
